import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, Search, Loader2, Plus, Sparkles, Trash2 } from "lucide-react";
import { PageHead, SectionHead } from "@/components/SectionHead";
import { EmptyState } from "@/components/EmptyState";

export default function DesignSearchPage() {
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [matches, setMatches] = useState(null);
  const [busy, setBusy] = useState(false);
  const uploadRef = useRef(null);
  const queryRef = useRef(null);
  const [meta, setMeta] = useState({ title: "", design_code: "", category: "", tags: "" });

  const load = async () => {
    const [s, l] = await Promise.all([
      api.get("/design-search/status").catch(()=>({data:null})),
      api.get("/design-search").catch(()=>({data:{items:[]}})),
    ]);
    setStatus(s.data);
    setItems(l.data.items || []);
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const params = new URLSearchParams();
      Object.entries(meta).forEach(([k,v])=>{ if(v) params.set(k, v); });
      await api.post(`/design-search/upload?${params.toString()}`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Design added"); setMeta({ title: "", design_code: "", category: "", tags: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); if (uploadRef.current) uploadRef.current.value = ""; }
  };

  const query = async (file) => {
    setBusy(true); setMatches(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/design-search/query?top_k=6", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setMatches(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); if (queryRef.current) queryRef.current.value = ""; }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this design?")) return;
    try { await api.delete(`/design-search/${id}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const isGdrive = status?.gdrive_configured;
  return (
    <div className="space-y-6" data-testid="design-search-page">
      <PageHead
        eyebrow="Catalog · Visual Search"
        title="Is this our design?"
        subtitle="Upload a photo; find matching pieces from the catalog."
        actions={
          <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${isGdrive ? "bg-[#E1F0E6] text-[#0F5E39] border border-[#0F5E39]/30" : "bg-amber-50 text-amber-800 border border-amber-200"}`}>
            <Sparkles size={12}/> {isGdrive ? "Drive connected" : "Manual mode (add GDRIVE_SERVICE_ACCOUNT_JSON for cloud-scale CLIP)"}
          </div>
        }
      />

      <SectionHead title="Query" subtitle="Snap or upload a photo — top 6 catalog matches with confidence score."/>
      <Card className="border-[#E4DFCF] rounded-2xl"><CardContent className="p-4 space-y-3">
        <input ref={queryRef} type="file" accept="image/*" capture="environment" onChange={(e)=>e.target.files?.[0] && query(e.target.files[0])} className="hidden" data-testid="design-query-input"/>
        <Button className="rounded-full bg-[#0C6E3D] hover:bg-[#08331E] text-white h-11 px-6" onClick={()=>queryRef.current?.click()} disabled={busy} data-testid="design-query-btn">
          {busy ? <Loader2 size={14} className="animate-spin mr-2"/> : <Search size={14} className="mr-2"/>}
          Upload photo to match
        </Button>
        {matches && (
          <div className="mt-3">
            <div className="text-xs text-[#4A5A50] mb-2">Matcher: {matches.matcher} · {matches.matches.length} hits</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {matches.matches.map((m)=>(
                <div key={m.id} className="rounded-xl border border-[#E4DFCF] p-2 bg-white">
                  <img src={fileUrl(m.thumb_url || m.image_url)} alt={m.title||""} className="w-full h-32 object-cover rounded-lg"/>
                  <div className="text-xs font-medium truncate mt-1">{m.title || m.design_code || "Untitled"}</div>
                  <div className="text-[10px] text-[#4A5A50]">Confidence {(m.confidence*100).toFixed(0)}%</div>
                </div>
              ))}
              {matches.matches.length === 0 && <div className="col-span-full text-sm text-[#4A5A50]">No matches — try adding designs below.</div>}
            </div>
          </div>
        )}
      </CardContent></Card>

      <SectionHead title="Add a design" subtitle="Upload catalog images with metadata; matcher indexes them instantly."/>
      <Card className="border-[#E4DFCF] rounded-2xl"><CardContent className="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Input placeholder="Title" value={meta.title} onChange={(e)=>setMeta({...meta,title:e.target.value})} className="h-11"/>
          <Input placeholder="Design code" value={meta.design_code} onChange={(e)=>setMeta({...meta,design_code:e.target.value})} className="h-11"/>
          <Input placeholder="Category (Ring, Necklace…)" value={meta.category} onChange={(e)=>setMeta({...meta,category:e.target.value})} className="h-11"/>
          <Input placeholder="tags, comma" value={meta.tags} onChange={(e)=>setMeta({...meta,tags:e.target.value})} className="h-11"/>
        </div>
        <input ref={uploadRef} type="file" accept="image/*" onChange={(e)=>e.target.files?.[0] && upload(e.target.files[0])} className="hidden" data-testid="design-upload-input"/>
        <Button variant="outline" className="rounded-full border-[#0C6E3D] text-[#0C6E3D] h-11" onClick={()=>uploadRef.current?.click()} disabled={busy} data-testid="design-upload-btn">
          <Upload size={14} className="mr-2"/> Upload design image
        </Button>
      </CardContent></Card>

      <SectionHead title={`Catalog (${items.length})`}/>
      {items.length === 0 ? (
        <EmptyState icon={Sparkles} eyebrow="Catalog empty" title="Add your first design" description="Upload real product photos to build the matcher's memory."/>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {items.map((d)=>(
            <div key={d.id} className="relative rounded-xl border border-[#E4DFCF] p-2 bg-white">
              <img src={fileUrl(d.thumb_url || d.image_url)} alt={d.title||""} className="w-full h-32 object-cover rounded-lg"/>
              <div className="text-xs font-medium truncate mt-1">{d.title || d.design_code || "Untitled"}</div>
              <div className="text-[10px] text-[#4A5A50]">{d.category || "—"}</div>
              <button onClick={()=>remove(d.id)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 hover:bg-red-50" title="Remove"><Trash2 size={12} className="text-red-600"/></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
